import ReactDOMServer from 'react-dom/server'
import { transformTimeStep, GraphActions } from 'utils'
import {
    calculateMinTide,
    hasShoal as checkHasShoal,
    formatNum,
    calculateExtraTimeOptions,
    splitWindowAtPeak,
    findPeakInWindow,
} from 'scenarios/calculations'
import { LatestTimeTemplate } from './templates/LatestTime'
import { DepartureRangeTemplate } from './templates/DepartureRange'

/**
 * Prepare departure options from analysis windows data.
 *
 * After DB migration 20251216000000 (swap_pilot_manoeuver_columns):
 *   - start/end_manoeuver_hour = shoal-based (sans pilote) times
 *   - For pilot scenarios, this is the narrower "Sans pilote" window
 *   - For non-pilot scenarios, this is the only window (dock-based)
 *
 * @param {object} a - Analysis object with departure_windows JSON
 * @returns {Object} { options: Array, wasEmpty: boolean }
 *   - options: Array of 4 departure options with { start, end, windowTooShort }
 *   - wasEmpty: true if departure_windows was empty/missing (used for pilot-only detection)
 */
function prepareDepartureOptions(a) {
    // Parse departure windows if available
    let windows = []
    if (a.departure_windows) {
        try {
            windows = JSON.parse(a.departure_windows)
        } catch (e) {
            console.error('Failed to parse departure_windows', e)
        }
    }

    // If we have windows data, use it
    if (windows.length > 0) {
        return {
            options: windows.slice(0, 4).map(w => ({
                start: w.start,
                end: w.end,
                windowTooShort: w.windowTooShort || false,
            })),
            wasEmpty: false,
        }
    }

    // Fallback: create option 1 from the selected manoeuver times
    const option1 = {
        start: a.start_manoeuver_hour,
        end: a.end_manoeuver_hour,
        windowTooShort: false,
    }

    return {
        options: [
            option1,
            { start: null, end: null },
            { start: null, end: null },
            { start: null, end: null },
        ],
        wasEmpty: true,
    }
}

/**
 * Calculate dual range options for Scenario 4 (departure_range_pilot_updown)
 * Splits each window into rising and descending portions at the peak.
 *
 * @param {Array} departureOptions - Array of departure options with { start, end }
 * @param {Array} highestTides - Array of tide peaks with { date, value }
 * @returns {Array} Array of dual range options with { rising, descending }
 */
function calculateDualRangeOptionsFromPeaks(departureOptions, highestTides) {
    if (!departureOptions || departureOptions.length === 0) {
        return []
    }

    return departureOptions.map(option => {
        if (!option.start || !option.end) {
            return { rising: null, descending: null }
        }

        const windowStart = new Date(option.start).getTime()
        const windowEnd = new Date(option.end).getTime()

        // Find the peak that falls within this window
        const peakInWindow = highestTides?.find(peak => {
            if (!peak?.date) return false
            const peakTime = new Date(peak.date).getTime()
            return peakTime >= windowStart && peakTime <= windowEnd
        })

        if (!peakInWindow) {
            // No peak found in window - can't split, return as descending only
            return {
                rising: null,
                descending: {
                    start: option.start,
                    end: option.end,
                    windowTooShort: option.windowTooShort || false,
                },
            }
        }

        const peakTime = new Date(peakInWindow.date).getTime()

        // Check if there's meaningful rising/descending portions
        const hasRising = peakTime > windowStart
        const hasDescending = peakTime < windowEnd

        const rising = hasRising
            ? {
                  start: option.start,
                  end: peakInWindow.date,
                  windowTooShort: peakTime - windowStart < 60 * 60 * 1000,
              }
            : null

        const descending = hasDescending
            ? {
                  start: peakInWindow.date,
                  end: option.end,
                  windowTooShort: windowEnd - peakTime < 60 * 60 * 1000,
              }
            : null

        return { rising, descending }
    })
}

/**
 * Prepare pilot departure options from analysis windows data.
 *
 * After DB migration 20251216000000 (swap_pilot_manoeuver_columns):
 *   - start/end_manoeuver_hour_pilot = dock-based (avec pilote) times
 *   - The *_pilot suffix now correctly means "with pilot"
 *   - This is the wider "Avec pilote" window (LPA pilot can handle the shoal)
 *
 * @param {object} a - Analysis object with departure_windows_pilot JSON
 * @returns {Array} Array of 4 pilot departure options with { start, end, windowTooShort }
 */
function preparePilotDepartureOptions(a) {
    // Parse pilot departure windows if available
    let windows = []
    if (a.departure_windows_pilot) {
        try {
            windows = JSON.parse(a.departure_windows_pilot)
        } catch (e) {
            console.error('Failed to parse departure_windows_pilot', e)
        }
    }

    // If we have windows data, use it
    if (windows.length > 0) {
        return windows.slice(0, 4).map(w => ({
            start: w.start,
            end: w.end,
            windowTooShort: w.windowTooShort || false,
        }))
    }

    // Fallback: create option 1 from the *_pilot manoeuver times (dock-based, avec pilote)
    const option1 = {
        start: a.start_manoeuver_hour_pilot,
        end: a.end_manoeuver_hour_pilot,
        windowTooShort: false,
    }

    return [
        option1,
        { start: null, end: null },
        { start: null, end: null },
        { start: null, end: null },
    ]
}

/**
 * Prepare props for email templates from analysis data
 * Replicates the data preparation logic from original HTMLTemplate
 *
 * @param {object} a - Analysis object
 * @param {Array} levelsAroundManoeuver_start - Tide data around start time
 * @param {Array} levelsAroundManoeuver_end - Tide data around end time
 * @param {string|null} image - Base64 encoded graph image (Sans pilote for pilot scenarios)
 * @param {string|null} imagePilot - Base64 encoded graph image for Avec pilote section
 * @returns {object} Props ready for template components
 */
export function prepareTemplateProps(
    a,
    levelsAroundManoeuver_start,
    levelsAroundManoeuver_end,
    image,
    imagePilot = null
) {
    const tides = JSON.parse(a.highest_tides).filter(t => t != null)

    // Get tide values at departure times
    const tide_at_departure_start = GraphActions.analysis.getTideAtHour(
        a.start_manoeuver_hour,
        levelsAroundManoeuver_start
    )
    const tide_at_departure_pilot_start = GraphActions.analysis.getTideAtHour(
        a.start_manoeuver_hour_pilot,
        levelsAroundManoeuver_start
    )
    const tide_at_departure_end = GraphActions.analysis.getTideAtHour(
        a.end_manoeuver_hour,
        levelsAroundManoeuver_end
    )
    const tide_at_departure_pilot_end = GraphActions.analysis.getTideAtHour(
        a.end_manoeuver_hour_pilot,
        levelsAroundManoeuver_end
    )

    // Calculate min tides
    const dockDepth = a.dock_depth || a?.dock?.depth || 0
    const dockShoal = a.dock_shoal || a?.dock?.shoal || 0
    const min_tide_depth_value = calculateMinTide(a.boat_draft, a.tolerance, dockDepth)
    const min_tide_shoal_value = calculateMinTide(a.boat_draft, a.tolerance, dockShoal)
    const hasShoal = checkHasShoal(a.dock_shoal)
    const min_tide = min_tide_depth_value > 0 ? formatNum(min_tide_depth_value) : '-'
    const min_tide_pilot = min_tide_shoal_value > 0 ? formatNum(min_tide_shoal_value) : '-'

    // Transform tides to 15-min intervals
    const tidesAroundDeparture = transformTimeStep(levelsAroundManoeuver_end, 15)

    // Prepare departure options for the new email format
    const {
        options: departureOptions,
        wasEmpty: departureWindowsWasEmpty,
    } = prepareDepartureOptions(a)
    const pilotDepartureOptions = preparePilotDepartureOptions(a)

    // Check scenario types - only explicit pilot scenarios (no legacy detection)
    const isPilotScenario =
        a.dock_calculation_method === 'departure_range_pilot_desc' ||
        a.dock_calculation_method === 'departure_range_pilot_updown'

    const isDualRangePilotScenario = a.dock_calculation_method === 'departure_range_pilot_updown'

    // Calculate dual range options for Scenario 4 (splits windows at peak into rising/descending)
    const dualRangeOptions = isDualRangePilotScenario
        ? calculateDualRangeOptionsFromPeaks(departureOptions, tides)
        : []

    // Calculate dual range options for pilot windows too (for Avec pilote section in Scenario 4)
    const pilotDualRangeOptions = isDualRangePilotScenario
        ? calculateDualRangeOptionsFromPeaks(pilotDepartureOptions, tides)
        : []

    // Calculate EXTRA time options for pilot scenarios (3 & 4)
    // These are the additional times available only with a LPA pilot
    //
    // BUG FIX: When departure_windows is empty (all peaks are pilot-only),
    // don't calculate extra times from fallback data - the comparison would be
    // between mismatched windows from different peaks.
    // Instead, return empty array and let Avec pilote section show full pilot windows.
    let pilotExtraTimeOptions = []
    if (isPilotScenario && !departureWindowsWasEmpty) {
        pilotExtraTimeOptions = calculateExtraTimeOptions(pilotDepartureOptions, departureOptions)
    }

    return {
        // Header props
        boatName: a.boat_name,
        dockLabel: a?.dock?.label || '-',

        // GeneralInfo props
        analysisDate: a.date,
        calculationMethod: a.dock_calculation_method,
        alias: a.alias,

        // ClearanceCalc props
        boatDraft: a.boat_draft,
        tolerance: a.tolerance,
        dockDepth: a.dock_depth,
        dockShoal: a.dock_shoal,
        hasShoal,
        minTide: min_tide,
        minTidePilot: min_tide_pilot,
        endManoeuverHour: a.end_manoeuver_hour,
        endManoeuverHourPilot: a.end_manoeuver_hour_pilot,

        // Summary props (legacy)
        times: {
            start: a.start_manoeuver_hour,
            end: a.end_manoeuver_hour,
            startPilot: a.start_manoeuver_hour_pilot,
            endPilot: a.end_manoeuver_hour_pilot,
        },
        tidesAtDeparture: {
            atStart: tide_at_departure_start,
            atEnd: tide_at_departure_end,
            atStartPilot: tide_at_departure_pilot_start,
            atEndPilot: tide_at_departure_pilot_end,
        },

        // DepartureOptions props (new format)
        departureOptions,
        pilotDepartureOptions,
        pilotExtraTimeOptions,
        // Scenario 4 dual range options (for Or/Ou format)
        dualRangeOptions,
        pilotDualRangeOptions,
        // Flag indicating if departure_windows was empty (all pilot-only peaks)
        departureWindowsWasEmpty,

        // Graph props
        image,
        imagePilot: imagePilot || image, // Fall back to main image if no separate pilot image

        // Table props
        highTides: tides,
        tidesAroundDeparture,
    }
}

/**
 * Generate email HTML using composable templates
 * Drop-in replacement for original HTMLTemplate
 *
 * @param {object} a - Analysis object
 * @param {Array} levelsAroundManoeuver_start - Tide data around start time
 * @param {Array} levelsAroundManoeuver_end - Tide data around end time
 * @param {string|null} image - Base64 encoded graph image (Sans pilote for pilot scenarios)
 * @param {string|null} imagePilot - Base64 encoded graph image for Avec pilote section
 * @returns {string} Rendered HTML string
 */
export function generateEmailHTML(
    a,
    levelsAroundManoeuver_start,
    levelsAroundManoeuver_end,
    image,
    imagePilot = null
) {
    const props = prepareTemplateProps(
        a,
        levelsAroundManoeuver_start,
        levelsAroundManoeuver_end,
        image,
        imagePilot
    )

    // Select template based on calculation method
    // Scenarios 2, 3, 4 all use DepartureRangeTemplate (with pilot sections for 3/4)
    const isDepartureRange =
        a.dock_calculation_method === 'departure_range' ||
        a.dock_calculation_method === 'departure_range_pilot_desc' ||
        a.dock_calculation_method === 'departure_range_pilot_updown'

    const Template = isDepartureRange ? DepartureRangeTemplate : LatestTimeTemplate

    return ReactDOMServer.renderToString(<Template {...props} />)
}

// Re-export sections and templates for direct use
export * from './sections'
export * from './templates'
